import React, { useState, useEffect } from 'react';
import { Copy } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkBreaks from 'remark-breaks';
import rehypeKatex from 'rehype-katex';
import { FileAttachment } from './FileAttachment';
import 'katex/dist/katex.min.css';

const LOADING_MESSAGES = [
  'Нейросеть ищет информацию',
  'Формирует ответ',
  'Анализирует контекст',
  'Обрабатывает запрос',
  'Генерирует текст',
  'Проверяет данные',
  'Изучает источники',
  'Собирает информацию',
  'Организует ответ',
  'Подбирает слова',
  'Структурирует мысли',
  'Проверяет факты',
  'Ищет релевантные данные',
  'Обдумывает ответ',
  'Создаёт контент',
  'Обрабатывает запрос',
  'Формирует структуру',
  'Адаптирует информацию',
  'Готовит ответ',
  'Завершает обработку',
];

interface MessageProps {
  message?: {
    id: string;
    type: 'human' | 'ai';
    content: Array<{ type: 'text'; text: string }>;
    attachments?: Array<{
      id: string;
      name: string;
      type: string;
      size: number;
      data?: string;
      url?: string;
      uploadedAt: Date;
    }>;
  };
  isLoading?: boolean;
  onViewAttachment?: (attachment: {
    id: string;
    name: string;
    type: string;
    size: number;
    data?: string;
    url?: string;
    uploadedAt: Date;
  }) => void;
}

export const Message: React.FC<MessageProps> = ({
  message,
  isLoading = false,
  onViewAttachment,
}) => {
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [textOpacity, setTextOpacity] = useState(1);
  
  const content = message?.content ?? [];
  const contentString = content
    .filter((c) => c.type === 'text')
    .map((c) => c.text)
    .join('');

  useEffect(() => {
    if (isLoading && !contentString) {
      const interval = setInterval(() => {
        setTextOpacity(0);
        
        setTimeout(() => {
          setLoadingTextIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
          setTextOpacity(1);
        }, 300);
      }, 5000);
      
      return () => clearInterval(interval);
    } else {
      setLoadingTextIndex(0);
      setTextOpacity(1);
    }
  }, [isLoading, contentString]);

  const copyToClipboard = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (contentString) {
      await navigator.clipboard.writeText(contentString);
      setTimeout(() => {
        const button = event.currentTarget;
        button.blur();
        if (document.activeElement === button) {
          (document.activeElement as HTMLElement).blur();
        }
      }, 2000);
    }
  };

  if (isLoading && !contentString) {
    return (
      <div className="chat-message chat-message--ai">
        <div className="chat-message__loading">
          <div 
            className="chat-message__loading-text"
            style={{ opacity: textOpacity }}
          >
            {LOADING_MESSAGES[loadingTextIndex]}
          </div>
          <div className="chat-message__loading-dots">
            <div className="chat-message__loading-dot"></div>
            <div className="chat-message__loading-dot"></div>
            <div className="chat-message__loading-dot"></div>
          </div>
        </div>
      </div>
    );
  }

  const isAI = message?.type === 'ai';
  const messageClass = `chat-message chat-message--${isAI ? 'ai' : 'human'}`;
  const bubbleClass = `chat-message__bubble--${isAI ? 'ai' : 'human'}`;

  return (
    <div className={messageClass}>
      <div className="chat-message__content">
        {(contentString.length > 0 || (message?.attachments && message.attachments.length > 0)) && (
          <div className={bubbleClass}>
            {contentString.length > 0 && (
              <div className="chat-message__text-content">
                {isAI ? (
                  <div className="chat-message__markdown">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkMath, remarkBreaks]}
                      rehypePlugins={[rehypeKatex]}
                      components={{
                        code: ({ className, children, ...props }) => {
                          const match = /language-(\w+)/.exec(className || '');
                          return match ? (
                            <pre className="chat-message__code-block">
                              <code className={className} {...props}>
                                {children}
                              </code>
                            </pre>
                          ) : (
                            <code className="chat-message__code-inline" {...props}>
                              {children}
                            </code>
                          );
                        },
                        p: ({ children }) => <p className="chat-message__paragraph">{children}</p>,
                        ul: ({ children }) => <ul className="chat-message__list">{children}</ul>,
                        ol: ({ children }) => <ol className="chat-message__list chat-message__list--ordered">{children}</ol>,
                        li: ({ children }) => <li className="chat-message__list-item">{children}</li>,
                        blockquote: ({ children }) => (
                          <blockquote className="chat-message__blockquote">
                            {children}
                          </blockquote>
                        ),
                        table: ({ children }) => (
                          <div className="chat-message__table-container">
                            <table className="chat-message__table">
                              {children}
                            </table>
                          </div>
                        ),
                        th: ({ children }) => (
                          <th className="chat-message__table-header">
                            {children}
                          </th>
                        ),
                        td: ({ children }) => (
                          <td className="chat-message__table-cell">{children}</td>
                        ),
                      }}
                    >
                      {contentString}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="chat-message__text">{contentString}</p>
                )}
              </div>
            )}
            
            {message?.attachments && message.attachments.length > 0 && (
              <div className="chat-message__attachments">
                {message.attachments.map((attachment) => (
                  <FileAttachment
                    key={attachment.id}
                    attachment={attachment}
                    showRemoveButton={false}
                    compactMode={true}
                    onView={onViewAttachment ? (att) => {
                      onViewAttachment({
                        ...att,
                        id: String(att.id),
                      });
                    } : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        <div className="chat-message__actions">
          <button
            onClick={copyToClipboard}
            className="chat-message__action-button"
            title="Копировать"
          >
            <Copy size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Message;
